import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getIdMap, mergeSets } from '../../helpers/helpers';

const toUserRow = (user, userDivisions) => ({
  ...user,
  subtitle: user.position,
  divisionIds: userDivisions[user.id] ?? [],
});

/** Every user as a resource row, keyed by id. */
export const useUserRowMap = () => {
  const users = useSelector((state) => state.users.users);
  const userDivisions = useSelector((state) => state.users.userDivisions);
  return useMemo(
    () => getIdMap(users.map((user) => toUserRow(user, userDivisions))),
    [users, userDivisions],
  );
};

/** Active users in the selected divisions, narrowed to `selectedUsers` when set. */
export default function useUserRows({ selectedUsers = [] } = {}) {
  const users = useSelector((state) => state.users.users);
  const userDivisions = useSelector((state) => state.users.userDivisions);
  const {
    selectedDivisions = new Set(),
    divisions = {},
  } = useSelector((state) => state.settings);

  return useMemo(() => {
    const selectedUsersSet = new Set(selectedUsers);
    const selectedDivUsers = Object
      .values(divisions)
      .filter(({ id }) => selectedDivisions.has(id))
      .map(({ users: divUsers = new Set() }) => divUsers);
    const mergedSelectedDivUsers = mergeSets(selectedDivUsers);
    return users
      .filter(({ id: userId, active }) => {
        if (selectedUsersSet.size && !selectedUsersSet.has(userId)) return false;
        return mergedSelectedDivUsers.has(userId) && active;
      })
      .map((user) => toUserRow(user, userDivisions));
  }, [users, userDivisions, selectedDivisions, divisions, selectedUsers]);
}
